import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Router } from '@angular/router';
import { EMPTY, from, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, map, max, startWith, switchMap, tap } from 'rxjs/operators';

import { getSweetAlertOptions } from '@commons/utility/util';

import { show_popup, TITLE_ERROR } from '@shared/utility/popup-error';

import { CustomResponseDto } from '@core/model/custom-response-dto';
import { EmailInfo } from '@core/model/email-info';
import { retryWithDelay } from '@core/operators/retry-delay';
import { ValidatorService } from '../validator.service';
import { EmailVerificationService } from './email-verification.service';
import levenshtein from 'js-levenshtein';
import Swal from 'sweetalert2';
import { INPUT_PATTERNS_CONSTANT } from './rapi-email.constant';

export class EmailErrorStateMatcher extends ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return control.dirty && form.invalid;
  }
}

@Component({
  selector: 'app-rapi-email',
  templateUrl: './rapi-email.component.html',
  styleUrls: ['./rapi-email.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RapiEmailComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RapiEmailComponent),
      multi: true,
    },
  ],
})
export class RapiEmailComponent implements OnInit, ControlValueAccessor, OnDestroy, OnChanges {
  form: FormGroup;
  subscriptions: Subscription[] = [];
  emailErrorStateMatcher = new EmailErrorStateMatcher();
  existingValidationMsg: string;
  @Output() public focusEventEmail: EventEmitter<void>;

  @ViewChild('emailInput', { static: false }) emailInput: ElementRef;
  knownDomains: string[] = [
    'gmail.com',
    'hotmail.com',
    'outlook.com',
    'yahoo.com',
    'yahoo.es',
    'misena.edu.co',
    'live.com',
    'msn.com',
    'unal.edu.co',
    'uniminuto.edu.co',
  ];
  emailOptions: Observable<string[]>;
  emailConfirmationOptions: Observable<string[]>;
  invalidEmailMsg: string;
  mismatchMsg: string;
  maxLength: number;
  id_detail: string;
  @Input() disableEmailControl: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private validatorService: ValidatorService,
    private emailVerificationService: EmailVerificationService,
    private router: Router
  ) {
    from(this.knownDomains)
      .pipe(max((val1, val2) => val1.length - val2.length))
      .subscribe((val) => (this.maxLength = val.length));
    this.form = this.formBuilder.group(
      {
        email: ['', [Validators.required, Validators.pattern(INPUT_PATTERNS_CONSTANT.EMAIL)]],
        emailConfirmation: ['', Validators.required, Validators.pattern(INPUT_PATTERNS_CONSTANT.EMAIL)],
      },
      {
        validator: this.validatorService.matchingInputsValidator('email', 'emailConfirmation'),
      }
    );

    this.subscriptions.push(
      this.form.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.form.controls['emailConfirmation'].statusChanges.subscribe((status) => {
      this.form.updateValueAndValidity();
    });

    this.focusEventEmail = new EventEmitter<void>();
  }

  ngOnInit() {
    this.emailOptions = this.emailControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value))
    );

    this.emailConfirmationOptions = this.emailConfirmationControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value))
    );
    this.id_detail = sessionStorage.getItem('cai');
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.disableEmailControl) {
      this.emailControl.disable();
    } else {
      this.emailControl.enable();
    }
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  get value(): EmailInfo {
    return this.form.value;
  }

  set value(value: EmailInfo) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get emailControl() {
    return this.form.controls.email;
  }

  get emailConfirmationControl() {
    return this.form.controls.emailConfirmation;
  }

  public sendFocusEvent(): void {
    this.focusEventEmail.emit();
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
      this.emailInput.nativeElement.focus();
    }
  }

  private _filter(value: string): string[] {
    let ret = [];
    if (value) {
      let email = value.split('@');
      let username = email[0];
      let domain = email.length > 1 ? email[1] : null;
      if (domain) {
        this._getSimilarDomains(this.knownDomains, domain).forEach((currDomain) => {
          ret.push(username + '@' + currDomain);
        });
      } else {
        this.knownDomains.forEach((currDomain) => {
          ret.push(username + '@' + currDomain);
        });
      }
    }
    return ret.slice(0, 6);
  }

  private _getDistance(str1: string, str2: string, metric: string): number {
    switch (metric) {
      case 'levenshtein':
        return levenshtein(str1, str2);
      case 'jaro-winkler':
        return this._jaroWinklerDistance(str1, str2);
    }
  }

  /**
   * Get the n domains similar to currentDomain
   * domainList can't be empty
   * @param domainList
   * @param currentDomain
   */
  private _getSimilarDomains(domainList: string[], currentDomain: string, metric = 'jaro-winkler'): string[] {
    let distances = new Map();
    let distance = 0;
    let mean = 0;
    domainList.forEach((currDomain) => {
      distance = 1 - this._getDistance(currentDomain, currDomain, metric);
      mean += distance;
      distances.set(currDomain, distance);
    });
    mean /= domainList.length;
    const mapSort1 = new Map([...distances.entries()].sort((a, b) => a[1] - b[1]).filter((item) => item[1] <= mean));
    let exactMatch = [...distances.entries()].find((item) => {
      return item[1] == 0;
    });
    if (exactMatch) {
      return [];
    }
    return [...mapSort1.keys()];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  validate(_: FormControl) {
    return this.form.valid ? null : { emailInfo: { valid: false } };
  }

  showExistenceMessage(message: string) {
    let options = getSweetAlertOptions(message, 'Iniciar sesión', true, 'Rectificar email');

    Swal.fire(options).then((result) => {
      if (result.value) {
        this.router.navigate(['/login']);
      } else {
        this.emailControl.setValue('');
        this.emailConfirmationControl.setValue('');
      }
    });
  }

  private _asyncValidateEmailAddress(
    email: string
  ): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> | null {
    return this.emailVerificationService.validateEmail(email).pipe(
      map((result: CustomResponseDto<boolean>) => {
        if (result.response == false) {
          this.invalidEmailMsg = result.message;
          return {
            invalidEmail: true,
          };
        } else {
          return null;
        }
      })
    );
  }

  private isValidPattern(email: string | null): boolean {
    return email ? INPUT_PATTERNS_CONSTANT.EMAIL.test(email) : false;
  }

  private isValidEmailMatch(email: string | null, confirmEmail: string | null): boolean {
    return email && confirmEmail && email === confirmEmail;
  }

  asyncValidateEmailPipe(): AsyncValidatorFn {
    return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
      if (!this.isValidPattern(control.value)) {
        return of({ pattern: true });
      }

      if (!this.isValidEmailMatch(this.form.controls['email'].value, this.form.controls['emailConfirmation'].value)) {
        return of({ mismatch: true });
      }

      return of(control.value).pipe(
        debounceTime(3000),
        switchMap((value) => {
          if (value) {
            return this._asyncValidateEmailAddress(value);
          }
        }),
        tap({
          next: () => {},
        }),
        switchMap((value) => {
          if (value == null) {
            if (
              this.emailControl.value &&
              this.emailConfirmationControl.value &&
              this.emailControl.value === this.emailConfirmationControl.value
            ) {
              return this.emailVerificationService.accountExistsByEmail(this.emailControl.value).pipe(
                map((result) => {
                  if (result.response == true) {
                    this.showExistenceMessage(result.message);
                    return {
                      existring: true,
                    };
                  } else {
                    return value;
                  }
                })
              );
            } else {
              return of({ email: true });
            }
          } else {
            return of(value);
          }
        }),
        retryWithDelay(1000, 3),
        catchError((error: Error) => {
          show_popup(
            TITLE_ERROR,
            'Lo sentimos, ocurrió un error al validar tu correo electronico. Inténtalo de nuevo  P-66'
          );
          this.emailConfirmationControl.setValue(null);
          return EMPTY;
        })
      );
    };
  }

  //TODO: <Arnold> A set of test cases must be done.
  /**
   * Calculates the jaro-winkler distance between two strings
   * @param str1
   * @param str2
   * Taken from https://github.com/jordanthomas/jaro-winkler
   */
  private _jaroWinklerDistance(str1: string, str2: string) {
    var m = 0;
    var settings = { caseSensitive: false };

    if (str1.length === 0 || str2.length === 0) {
      return 0;
    }

    // Convert to upper if case-sensitive is false.
    if (!settings.caseSensitive) {
      str1 = str1.toUpperCase();
      str2 = str2.toUpperCase();
    }

    // Exit early if they're an exact match.
    if (str1 === str2) {
      return 1;
    }

    var range = Math.floor(Math.max(str1.length, str2.length) / 2) - 1;
    var str1Matches = new Array(str1.length);
    var str2Matches = new Array(str2.length);

    for (var i = 0; i < str1.length; i++) {
      var low = i >= range ? i - range : 0;
      var high = i + range <= str2.length - 1 ? i + range : str2.length - 1;

      for (var j = low; j <= high; j++) {
        if (str1Matches[i] !== true && str2Matches[j] !== true && str1[i] === str2[j]) {
          ++m;
          str1Matches[i] = str2Matches[j] = true;
          break;
        }
      }
    }

    // Exit early if no matches were found.
    if (m === 0) {
      return 0;
    }

    // Count the transpositions.
    var k = 0;
    var numTrans = 0;

    for (var i = 0; i < str1.length; i++) {
      if (str1Matches[i] === true) {
        for (var j = k; j < str2.length; j++) {
          if (str2Matches[j] === true) {
            k = j + 1;
            break;
          }
        }

        if (str1[i] !== str2[j]) {
          ++numTrans;
        }
      }
    }

    var weight = (m / str1.length + m / str2.length + (m - numTrans / 2) / m) / 3;
    var l = 0;
    var p = 0.1;

    if (weight > 0.7) {
      while (str1[l] === str2[l] && l < 4) {
        ++l;
      }

      weight = weight + l * p * (1 - weight);
    }

    return weight;
  }

  blur() {
    this.form.controls['emailConfirmation'].setAsyncValidators([this.asyncValidateEmailPipe()]);
    this.emailConfirmationControl.setValue(this.emailConfirmationControl.value);
  }

  focus() {
    this.form.controls['emailConfirmation'].clearAsyncValidators();
    this.form.controls['emailConfirmation'].updateValueAndValidity();
  }
}
